<template>
  <div class="development-search-filter-row">
    <div class="container">
      <div class="__filter-item-container">
        <!-- Search input field -->
        <ElementsCityboundSearch v-model="mapBounds" class="__input-search" label="Location" @update:model-value="updateRouteQuery()" />

        <!-- Property Type -->
        <ElementsInputsDropdown v-model="selectedDevelopmentType" class="__filter-dropdown __property-type" label="Type" :items="developmentTypeItems" @update:model-value="updateRouteQuery()" />
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import {Ref} from 'vue';
import {DropdownItem} from '~/@types/common';
import DevelopmentSearchQueryHandler from '~/composables/developmentSearchQuery';

// DATA Definitions
//----------------------------------------------------------------------------------------------------------------------
const searchKeyword: Ref<string> = ref('');
const selectedDevelopmentType: Ref<'Sub-division' | 'Apartment' | 'Terrace Homes ' | ''> = ref('');
const hasMounted: Ref<boolean> = ref(false);
const mapBounds: Ref<string> = ref('');
const developmentTypeItems: Ref<DropdownItem[]> = ref([
  {label: 'All', value: ''},
  {label: 'Sub-division', value: 'Sub-division'},
  {label: 'Apartment', value: 'Apartment'},
  {label: 'Terrace Homes ', value: 'Terrace Homes '},
]);

updateFilterValues();


/**
 * Component MOUNTED!
 */
onMounted(() => {
  nextTick(() => {
    hasMounted.value = true;
  });
});

/**
 * Update listing filters based on filter values
 */
function updateRouteQuery () {
  // If yet not mounted, do not change route query based on filter values
  if (!hasMounted.value) return;
  DevelopmentSearchQueryHandler.set({
    developmentType: selectedDevelopmentType.value,
    mapBounds: mapBounds.value,
  });
}

/**
 * Update the filter values based on the route query values
 */
function updateFilterValues () {
  const routeQuery = DevelopmentSearchQueryHandler.get();
  searchKeyword.value = routeQuery.keyword;
  selectedDevelopmentType.value = routeQuery.developmentType;
}
</script>

<style lang="scss">
.development-search-filter-row {
  margin-top: 4px;
  height: 60px;
  background: #ffffff;

  .__filter-item-container {
    display: inline-flex;
    align-items: center;
    max-height: 60px;
    border-right: 1px solid rgba(var(--text-lighter-1), 0.3);
    position: relative;
    width: 100%;
    flex-direction: row;
    justify-content: space-evenly;

    // Search
    .__input-search {
      min-width: 360px;
      border-left: 1px solid rgba(var(--text-lighter-1), 0.3);
      height: 60px;
      margin-right: 12px;
    }

    // Dropdown items
    .__filter-dropdown {
      height: 60px;
      border-left: 1px solid rgba(var(--text-lighter-1), 0.3);
      padding-top: 5px;
      padding-bottom: 0;
      min-width: 120px;

      .__input-dropdown-inner {
        min-height: unset;
        border: none;
        padding-top: 2px;

        .__selection {
          color: rgb(var(--text-darken-1));
        }
      }

      .__dropdown-label {
        color: rgb(var(--text-lighter-1));
        margin-bottom: 0;
        font-weight: 400;
        padding: 5px 12px;
      }

      .__dropdown-items-wrapper {
        top: 55px;
      }

      &.__property-type {
        min-width: 160px;
      }
    }

    // Budget filter
    .__filter-budget {
      cursor: pointer;
      position: relative;
      border-left: 1px solid rgba(var(--text-lighter-1), 0.3);
      padding: 5px 12px 12px;
      min-width: 120px;
      height: 60px;
      margin-right: 12px;

      .__label {
        color: rgb(var(--text-lighter-1));
        margin-bottom: 0;
        font-weight: 400;
        font-size: 14px;
        padding: 5px 0;
        display: block;
      }

      .__budget-inner {
        display: inline-flex;
        margin-top: 3px;

        .__value {
          font-size: 16px;
          color: rgb(var(--text-darken-1));
        }

        img {
          position: absolute;
          right: 0;
          bottom: 15px;
          transform: rotate(0deg);
          transform-origin: center center;
          transition: all 0.2s ease-in;
        }
      }

      &.--opened {
        .__budget-inner {
          img {
            transform: rotate(180deg);
            transition: all 0.2s ease-in;
          }
        }
      }
    }

    .__filter-range-slider {
      max-height: 0;
      overflow: hidden;
      position: absolute;
      background: #fff;
      z-index: 999;
      border-bottom-right-radius: 8px;
      border-bottom-left-radius: 8px;
      box-shadow: 0 0 8px -2px #b4b4b4;
      min-width: 780px;
      max-width: 100%;
      width: 100%;
      right: -12px;
      top: 60px;

      .__budget-range {
        padding: 12px 12px 18px;
      }

      &.--opened {
        max-height: 200px;
        overflow: auto;
        transition: max-height 0.4s cubic-bezier(.79, .14, .15, .86);
      }
    }

    // Other search trigger
    .__btn-slider-icon {
      padding: 0 12px 0 15px;
      border-left: 1px solid rgba(var(--text-lighter-1), 0.3);
      height: 60px;
    }

    .blocks-other-search-popup {
      top: 60px;
      z-index: 999;
    }
  }

  // Responsive
  // -------------------------------------------------------------------------------------------------------------------
  @media (max-width: $grid-xl) {
    .container {
      max-width: unset;
      padding: 0;
      width: 100% !important;

      .__filter-item-container {
        .__input-search {
          min-width: 285px;
        }

        .__filter-dropdown {
          min-width: 120px;
          width: fit-content;

          &.__property-type {
            min-width: 140px;
          }
        }
      }
    }
  }

  @media screen and (max-width: $grid-sm) {
    .container {
      .__filter-item-container {
        .__input-search {
          min-width: 160px;
        }

        .__input-text-inner {
          .__input-field-wrapper {
            .__input-label {
              padding-left: 8px;
            }

            input {
              margin-top: -6px;
            }

            .__prepend-icon-wrapper {
              bottom: 8px;
            }
          }
        }
      }
    }
  }

  @media (max-width: 350px) {
    .container {
      .__filter-item-container {
        .__input-search {
          min-width: 140px;

          .__input-text-inner {
            .__input-field-wrapper {
              .__input-label {
                padding-left: 8px;
              }

              input {
                margin-top: -6px;
              }

              .__prepend-icon-wrapper {
                bottom: 8px;
              }
            }
          }
        }

        .__filter-dropdown {
          min-width: 120px;

          &.__property-type {
            min-width: 140px;
          }
        }
      }
    }
  }
}
</style>
